import { useCallback, useEffect, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ProviderStatus } from '@studio/shared';

import { account, api, type AccountOverview, type SessionUser } from './lib/api';
import { useT } from './lib/i18n';
import { useSession } from './lib/session';
import { Palette } from './components/Palette';
import { Preferences, useAccountPreferences } from './components/Preferences';
import { ChartIcon, ChevronIcon, PlusIcon, SignOutIcon, SlidersIcon } from './components/Icons';
import { AdminPage } from './pages/Admin';
import { GalleryPage } from './pages/Gallery';
import { ModelsPage } from './pages/Models';
import { NewShootPage } from './pages/NewShoot';
import { NewShootHubPage } from './pages/NewShootHub';
import { NewShootWizard } from './pages/Wizard';
import { CopyShootPage } from './pages/CopyShoot';
import { StylistPage } from './pages/Stylist';
import { SettingsPage } from './pages/Settings';
import { LegalPage } from './pages/Legal';
import { ShootViewPage } from './pages/ShootView';
import { ShootsPage } from './pages/Shoots';
import { SignInPage } from './pages/SignIn';
import { VerifyPage } from './pages/Verify';
import { useToast } from './components/Toaster';
import { auth } from './lib/api';

export function App() {
  const t = useT();
  const { user, authDisabled, suspended, verificationRequired } = useSession();
  useAccountPreferences();
  const [status, setStatus] = useState<ProviderStatus | undefined>();
  const location = useLocation();
  const toast = useToast();

  useEffect(() => {
    if (!authDisabled && !user) return;
    void api
      .status()
      .then(setStatus)
      .catch(() => undefined);
  }, [authDisabled, user]);

  // The terms and the privacy notice are read before there is an account.
  if (location.pathname === '/terms' || location.pathname === '/privacy') {
    return <LegalPage kind={location.pathname === '/terms' ? 'terms' : 'privacy'} />;
  }

  // Still asking the server who this is. Rendering nothing for that moment is
  // better than flashing the sign-in page at someone who is already signed in.
  if (user === undefined && !authDisabled) return <div className="app app--waiting" />;

  // The confirmation link works signed in or out, and needs no chrome.
  if (location.pathname === '/verify' && !authDisabled) return <VerifyPage />;

  // Signed out: the sign-in screen answers every path, so a reset link
  // (/reset?token=…) lands on it without needing a route of its own.
  if (user === null && !authDisabled) return <SignInPage />;

  const unverified = Boolean(user && verificationRequired && !user.emailVerified);
  const resend = async () => {
    try {
      await auth.resendVerification();
      toast(t('auth.verifySent'), 'ok');
    } catch {
      toast(t('auth.failed'), 'error');
    }
  };

  return (
    <div className="app">
      <a className="skip-link" href="#main">
        {t('app.skip')}
      </a>

      <TopNav user={user} status={status} />

      <main className="app__body" id="main" tabIndex={-1}>
        {/* An account that has been stopped should be told, not left to
            wonder why every page is empty. */}
        {suspended && (
          <div className="app__inner" style={{ paddingBottom: 0 }}>
            <div className="banner banner--error">{t('error.account_suspended')}</div>
          </div>
        )}
        {/* Rendering waits for a confirmed address; everything else works,
            and the way to get the link again is right here. */}
        {unverified && !suspended && (
          <div className="app__inner" style={{ paddingBottom: 0 }}>
            <div className="banner">
              {t('auth.verifyBanner', { email: user!.email })}{' '}
              <button type="button" className="btn btn--ghost btn--sm" onClick={resend}>
                {t('auth.verifyResend')}
              </button>
            </div>
          </div>
        )}
        <Routes>
          {/* The studio opens on the way into a shoot: a new roll is what you came for. */}
          <Route path="/" element={<Navigate to="/new" replace />} />
          <Route path="/new" element={<NewShootHubPage />} />
          <Route path="/new/copy" element={<CopyShootPage />} />
          <Route path="/new/presets" element={<NewShootWizard />} />
          <Route path="/new/advanced" element={<NewShootPage />} />
          <Route path="/new/brief" element={<StylistPage />} />
          <Route path="/models" element={<ModelsPage />} />
          <Route path="/shoots" element={<ShootsPage />} />
          <Route path="/shoots/:id" element={<ShootViewPage />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

/** Closes a popover on a press anywhere else, or on Escape. */
function useDismiss(open: boolean, close: () => void) {
  useEffect(() => {
    if (!open) return;
    const key = (event: KeyboardEvent) => event.key === 'Escape' && close();
    window.addEventListener('pointerdown', close);
    window.addEventListener('keydown', key);
    return () => {
      window.removeEventListener('pointerdown', close);
      window.removeEventListener('keydown', key);
    };
  }, [open, close]);
}

/**
 * The bar across the top of every page.
 *
 * Three places you keep things as words with a line under the one you are in;
 * search; the one thing you come to do — a new shoot — as the only filled
 * button, opening onto its three ways in; and you, as a black disc that opens
 * the account. On a phone the words do not fit, so the bar keeps the mark and
 * a round button that opens the whole menu over the page.
 */
function TopNav({ user, status }: { user: SessionUser | null | undefined; status?: ProviderStatus }) {
  const t = useT();
  const location = useLocation();
  const { signOut } = useSession();
  const [overview, setOverview] = useState<AccountOverview | undefined>();
  const [developing, setDeveloping] = useState(0);
  const [open, setOpen] = useState<'new' | 'account' | 'mobile' | null>(null);
  const close = useCallback(() => setOpen(null), []);
  useDismiss(open === 'new' || open === 'account', close);

  // Whatever was open closes when the page changes under it.
  useEffect(() => setOpen(null), [location.pathname]);

  useEffect(() => {
    if (!user) return;
    void account
      .overview()
      .then(setOverview)
      .catch(() => undefined);
  }, [user]);

  /*
   * How many rolls are still developing, for the count beside Séances.
   * Polled, because a render finishes on the server without telling anyone,
   * and slowly — this is a reassurance, not an instrument.
   */
  useEffect(() => {
    if (!user) return;
    let live = true;
    const count = () =>
      void api
        .listShoots()
        .then((rolls) => live && setDeveloping(rolls.filter((roll) => roll.running).length))
        .catch(() => undefined);
    count();
    const timer = window.setInterval(count, 20_000);
    return () => {
      live = false;
      window.clearInterval(timer);
    };
  }, [user]);

  const initial = user ? (user.name || user.email).slice(0, 1).toUpperCase() : '·';
  const toggle = (which: 'new' | 'account' | 'mobile') => setOpen((was) => (was === which ? null : which));
  const ways = [
    { to: '/new/copy', key: 'copy' },
    { to: '/new/presets', key: 'presets' },
    { to: '/new/brief', key: 'brief' },
  ] as const;

  return (
    <header className={`topnav ${open === 'mobile' ? 'is-menu' : ''}`}>
      <NavLink to="/new" className="topnav__brand">
        studio<span>.</span>
      </NavLink>

      <nav className="topnav__links" aria-label={t('app.sections')}>
        <NavLink to="/models" className={navClass}>
          {t('app.nav.talent')}
        </NavLink>
        <NavLink to="/shoots" className={navClass}>
          {t('app.nav.rolls')}
          {developing > 0 && (
            <span className="topnav__badge" aria-label={t('rolls.status.rolling')}>
              {developing}
            </span>
          )}
        </NavLink>
        <NavLink to="/gallery" className={navClass}>
          {t('app.nav.sheet')}
        </NavLink>
      </nav>

      <div className="topnav__end">
        <Palette />

        <div className="topnav__pop" onPointerDown={(event) => event.stopPropagation()}>
          <button
            type="button"
            className="btn btn--primary topnav__new"
            aria-haspopup="menu"
            aria-expanded={open === 'new'}
            onClick={() => toggle('new')}
          >
            <PlusIcon />
            <span>{t('app.nav.shoot')}</span>
            <ChevronIcon />
          </button>
          {open === 'new' && (
            <div className="dropdown dropdown--ways" role="menu">
              {ways.map((way, index) => (
                <NavLink key={way.key} to={way.to} role="menuitem" className={`dropdown__way way-${way.key}`}>
                  <span className="dropdown__num">{String(index + 1).padStart(2, '0')}</span>
                  <span className="dropdown__title">{t(`hub.${way.key}.title`)}</span>
                  <span className="dropdown__text">{t(`hub.${way.key}.text`)}</span>
                </NavLink>
              ))}
            </div>
          )}
        </div>

        {user && (
          <div className="topnav__pop" onPointerDown={(event) => event.stopPropagation()}>
            <button
              type="button"
              className={`topnav__me ${open === 'account' ? 'is-on' : ''}`}
              aria-label={t('app.menu')}
              aria-haspopup="menu"
              aria-expanded={open === 'account'}
              onClick={() => toggle('account')}
            >
              {initial}
            </button>
            {open === 'account' && (
              <div className="dropdown dropdown--account" role="menu" aria-label={t('app.menu')}>
                <div className="dropdown__who">
                  <span className="topnav__me" aria-hidden="true">
                    {initial}
                  </span>
                  <span className="dropdown__whoText">
                    <strong>{user.name || user.email}</strong>
                    <span>{user.email}</span>
                  </span>
                </div>
                {overview && (
                  <div className="dropdown__usage">
                    <span>{t('app.frames')}</span>
                    <b>{overview.usage.frames}</b>
                  </div>
                )}
                <Preferences />
                <details className="signal">
                  <summary>{t('app.signal')}</summary>
                  <Led label={t('app.signal.styling')} slot={status?.text} />
                  <Led label={t('app.signal.analysis')} slot={status?.vision} />
                  <Led label={t('app.signal.render')} slot={status?.image} />
                </details>
                <div className="dropdown__sep" />
                <NavLink to="/settings" role="menuitem" className="dropdown__item">
                  <SlidersIcon />
                  {t('settings.title')}
                </NavLink>
                {/* Only staff are offered it; the server decides again on every call. */}
                {user.staff && (
                  <NavLink to="/admin" role="menuitem" className="dropdown__item">
                    <ChartIcon />
                    {t('admin.title')}
                  </NavLink>
                )}
                <button
                  type="button"
                  role="menuitem"
                  className="dropdown__item"
                  onClick={() => void signOut()}
                >
                  <SignOutIcon />
                  {t('settings.signOut')}
                </button>
              </div>
            )}
          </div>
        )}

        <button
          type="button"
          className={`topnav__burger ${open === 'mobile' ? 'is-x' : ''}`}
          aria-label={t(open === 'mobile' ? 'app.railClose' : 'app.railOpen')}
          aria-expanded={open === 'mobile'}
          onClick={() => toggle('mobile')}
        >
          <i />
          <i />
        </button>
      </div>

      {open === 'mobile' && (
        <nav className="mobilemenu" aria-label={t('app.sections')}>
          {[
            { to: '/new', label: t('app.nav.shoot') },
            { to: '/models', label: t('app.nav.talent') },
            { to: '/shoots', label: t('app.nav.rolls') },
            { to: '/gallery', label: t('app.nav.sheet') },
          ].map((link, index) => (
            <NavLink key={link.to} to={link.to} end={link.to === '/new'} className={navClass}>
              <small>{String(index + 1).padStart(2, '0')}</small>
              <span style={{ animationDelay: `${120 + index * 60}ms` }}>{link.label}</span>
            </NavLink>
          ))}
          <div className="mobilemenu__foot">
            {user && (
              <div className="mobilemenu__who">
                <span className="topnav__me" aria-hidden="true">
                  {initial}
                </span>
                <span>
                  <strong>{user.name || user.email}</strong>
                  <span>{user.email}</span>
                </span>
              </div>
            )}
            <div className="mobilemenu__actions">
              <NavLink to="/settings" className="btn">
                {t('settings.title')}
              </NavLink>
              {user?.staff && (
                <NavLink to="/admin" className="btn">
                  {t('admin.title')}
                </NavLink>
              )}
              <button type="button" className="btn btn--invert" onClick={() => void signOut()}>
                {t('settings.signOut')}
              </button>
            </div>
          </div>
        </nav>
      )}
    </header>
  );
}

function Led({ label, slot }: { label: string; slot?: ProviderStatus['text'] }) {
  const t = useT();
  const live = slot?.configured ?? false;
  return (
    <div className="led-row" title={slot ? `${slot.provider} · ${slot.model}` : t('app.signal.checking')}>
      <span className={`led ${slot ? (live ? 'led--live' : 'led--mock') : ''}`} />
      <span>{label}</span>
      <span className="led-row__value">
        {slot ? t(live ? 'app.signal.live' : 'app.signal.preview') : '—'}
      </span>
    </div>
  );
}

const navClass = ({ isActive }: { isActive: boolean }) => (isActive ? 'is-active' : '');
